import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../services/languageservice/language.service';
import { GeoLocationService, LocationData, PermissionStatus } from '../../../services/geo-location.service';

@Component({
  selector: 'app-location-widget',
  templateUrl: './location-widget.component.html',
  styleUrls: ['./location-widget.component.scss'],
})
export class LocationWidgetComponent implements OnInit, OnDestroy {
  @Input() isExpanded: boolean = false;
  @Output() toggleExpanded = new EventEmitter<void>();

  currentLocation: LocationData | null = null;
  error: string | null = null;
  isLoading = false;
  permissionStatus: PermissionStatus = 'unknown';
  locationSubscription: Subscription | null = null;

  constructor(
    private languageService: LanguageService,
    private geoLocationService: GeoLocationService
  ) {}

  ngOnInit() {
    this.checkLocationPermission();
  }

  ngOnDestroy() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  async checkLocationPermission() {
    const result = await this.geoLocationService.checkLocationPermission();
    this.permissionStatus = result.status;
    this.error = result.error || null;
    
    if (result.status === 'granted') {
      this.getCurrentLocation();
    } else if (result.status === 'prompt') {
      // For web platform, if we get 'prompt' status, we should try to get location
      // which will trigger the permission dialog
      this.getCurrentLocation();
    }
  }

  async requestLocationPermission() {
    try {
      this.isLoading = true;
      this.error = null;
      
      const result = await this.geoLocationService.requestLocationPermission();
      this.permissionStatus = result.status;
      this.error = result.error || null;
      if (this.permissionStatus === 'granted') {
        this.getCurrentLocation();
      } else if(this.permissionStatus === 'denied') {
        this.geoLocationService.openLocationSettings();
      }
    } catch (error) {
      console.error('Error requesting location permission:', error);
      this.error = 'widgets.location.error';
    } finally {
      this.isLoading = false;
    }
  }



  async getCurrentLocation() {
    try {
      this.isLoading = true;
      this.error = null;
      
      const result = await this.geoLocationService.getCurrentLocation();
      
      if (result.location) {
        this.currentLocation = result.location;
        this.permissionStatus = 'granted';
      } else {
        this.error = result.error || 'widgets.location.error';
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      this.error = 'widgets.location.error';
    } finally {
      this.isLoading = false;
    }
  }


  get getMapUrl(): string {
    if (!this.currentLocation) return '';
    return this.geoLocationService.getMapUrl(this.currentLocation);
  }

  get getCoordinatesText(): string {
    if (!this.currentLocation) return '';
    return this.geoLocationService.getCoordinatesText(this.currentLocation);
  }



  refreshLocation() {
    if (this.permissionStatus === 'granted') {
      this.getCurrentLocation();
    } else {
      this.requestLocationPermission();
    }
  }

  async copyCoordinates() {
    if (!this.currentLocation) {
      return;
    }

    await this.geoLocationService.copyCoordinates(this.currentLocation);
  }

  onToggleExpanded() {
    this.toggleExpanded.emit();
  }
}
